import React, { useState, useEffect } from 'react';
import { cloneDeep, isEqual } from 'lodash';

import { formatValue } from 'components/base/common/utils';
import { dateService } from 'services/date';
import { getClass } from 'utils/component-helpers';
import { ratesService } from './rates-service';
import { session } from 'services/session';

import RetailerOverviewRow from './retailer-overview-row';
import StrategyInfoModal from './strategy-info-modal';
import StrategyOverviewRow from './strategy-overview-row';
import SplitSortDropdown from '../../base/dropdowns/split-sort-dropdown';

import sortAscIcon from 'images/icons/sort-ascend.svg';
import sortDescIcon from 'images/icons/sort-descend.svg';

import './overview-table.scss';

const paymentModels = {
	1: {label: 'CPC', descriptors: ['Flat EPC']}, // CPC
	2: {label: 'CPA', descriptors: ['Last-click 1-7 days']}, // CPA
	4: {label: 'CPA+', descriptors: ['Post-click 30', 'No returns']}, // EPP
	5: {label: 'Dynamic EPC', descriptors: ['Post-click 30', 'No returns']}, // ???
};

// Unsortable columns for coming soon tab
const disabledColumns = ['Your Clicks', 'Your Earnings', 'Your EPC'];
const unsortableColumns = [''];

const OverviewTable = ({
	data, columnHeaders, tab, sortOptions, sortBy, setSortBy, headerSort, setHeaderSort,
	currentHeader, setCurrentHeader, overview, retailer,
}) => {
	const strategyLabelMap = {
		product_launch: 'Product Launch',
		sale: 'Sale',
		vendor_funded: 'Vendor Funded',
		share_of_voice: 'SOV',
		preferred_publisher: 'Preferred Publisher',
		growth_incentive: 'Growth Incentive',
	};

	const [didMount, setDidMount] = useState(false);
	const [dataRows, setDataRows] = useState([]);
	const [loading, setLoading] = useState(true);
	const [modalStrategy, setModalStrategy] = useState(null);
	const [sortDirection, setSortDirection] = useState(sortBy.direction);
	const [currentTab, setCurrentTab] = useState(tab);
	const sortOptionsByLabel = sortOptions.reduce((obj, item) => {
		const {label, value, direction, dropdown} = item;
		return Object.assign(obj, {[label]: {label, value, direction, dropdown}});
	}, {});

	useEffect(() => {
		setDidMount(true);
	}, []);

	useEffect(() => {
		setLoading(true);
		formatData();
	}, [data, tab]);

	useEffect(() => {
		if (didMount) {
			if (
				!isEqual(headerSort.value, sortBy.value)
				|| (!isEqual(sortDirection, sortBy.direction)
				&& (isEqual(currentTab, tab)))
			) {
				const currentSort = cloneDeep(sortOptionsByLabel[currentHeader]);
				currentSort.value = headerSort.value;
				const dropdownDirection = !isEqual(sortDirection, sortBy.direction)
					|| !isEqual(headerSort.value, sortBy.value);
				currentSort.direction = getDirection(currentHeader, dropdownDirection);
				setSortBy(currentSort);
				setCurrentTab(tab);
			}
		}
	}, [headerSort, sortDirection]);

	async function formatData() {
		setDataRows(
			await data.reduce((dataRows, dataRow) => {
				const strategyRate = dataRow.strategies ? dataRow.strategies.top_publisher_commission_rate : null;
				const strategyPricingModel = dataRow.strategies ? dataRow.strategies.pricing_model : null;
				const formattedRates = ratesService.formatStrategyRates(dataRow);
				const isUpcoming = dataRow.strategy_status === 'upcoming';
				return ratesService.isVisible(dataRow, overview) ? dataRows.concat([{
					...dataRow,
					strategy_name: dataRow.strategy_name || '',
					strategy_description: dataRow.strategy_description || 'Details Coming Soon',
					strategy_labels: dataRow.strategy_labels ? getStrategyLabels(dataRow.strategy_labels) : null,
					fee_type: dataRow.strategy_fee_type ? dataRow.strategy_fee_type : null,

					// Format baseline rate and fetch payment model
					commission_rate: formattedRates.commission_rate,
					evergreen_rate: formattedRates.evergreen_rate,
					payment_model: dataRow.pricing_model && paymentModels[dataRow.pricing_model]
						? paymentModels[dataRow.pricing_model].label
						: paymentModels[5].label,

					// Format strategy rate
					strategic_rate: strategyRate
						? strategyPricingModel && strategyPricingModel === 1
							? `${formatValue(strategyRate, 'currency')} per click`
							: `${formatValue(strategyRate, 'percent', 'commission_rate')} Commission`
						: null,

					// for CPC strategies
					strategy_bid: formattedRates.strategy_bid,

					// Format performance column values
					publisher_epc: formatValue(dataRow.publisher_epc, 'currency'),
					clicks: formatValue(dataRow.clicks),
					publisher_total_earnings: formatValue(
						dataRow.publisher_total_earnings, 'currency', 'publisher_total_earnings'
					),

					active_since: isUpcoming
						? 'Coming Soon'
						: dataRow.strategy_datetime_start
							? `Active since ${dataRow.strategy_datetime_start.split('T')[0]}`
							: '',

					strategy_date: isUpcoming
						? dataRow.strategy_datetime_start
							? `Expected ${dataRow.strategy_datetime_start.split('T')[0]}`
							: 'Details Coming Soon'
						: dataRow.strategy_datetime_end
							? `Expires ${dataRow.strategy_datetime_end.split('T')[0]}`
							: `Doesn't Expire`,
					strategy_date_label: dataRow.strategy_name ? getDateLabel(dataRow) : null,
					strategy_status: dataRow.strategy_name
						? ratesService.getStrategyStatus(dataRow)
						: null,
				}]) : dataRows;
			}, [])
		);
		setLoading(false);
	}

	/**
	 * The text returned depends on the strategy's start date and end date
	 * If the strategy is paused, return "-"
	 * else if the startdate is in the future return "Coming on MM/DD/YYYY"
	 * else if the strategy has no end date return "Doesn't Expire"
	 * else if the strategy has end date and the end date is in the past return "Expired"
	 * else strategy is active but will expire return "Expires in MM/DD/YYYY"
	 */
	function getDateLabel(strategy) {
		const start = strategy.strategy_datetime_start;
		const end = strategy.strategy_datetime_end;
		const strategyStatus = ratesService.getStrategyStatus(strategy);
		const todayUtc = dateService.getDateTime().utc().format();

		const [hoursDifference, daysDifference, monthsDifference] = ['hour', 'day', 'month'].map((unit) => (
			dateService.subtractDates(end, todayUtc, unit) - 1
		));

		const roundedHours = Math.round(hoursDifference);
		const roundedDays = Math.round(daysDifference);
		const roundedMonths = Math.floor(monthsDifference);

		const quantityUnits = daysDifference < 2
			? `${roundedHours} hour${roundedHours !== 1 ? 's' : ''}`
			: daysDifference < 61
				? `${roundedDays} day${roundedDays !== 1 ? 's' : ''}`
				: `${roundedMonths}+ month${roundedMonths !== 1 ? 's' : ''}`;

		return strategyStatus === 'paused'
			? '-'
			: strategyStatus === 'upcoming'
				? `Coming on ${dateService.formatDate(start, 'MM/DD/YYYY', true)}`
				: !end
					? `Doesn't expire`
					: strategyStatus === 'expired'
						? `Expired on ${dateService.formatDate(end, 'MM/DD/YYYY', true)}`
						: `Expires in ${quantityUnits}`;
	}

	function getStrategyLabels(labels) {
		const mappedLabels = labels.map((label) => strategyLabelMap[label]);
		return mappedLabels ? mappedLabels.join(', ') : null;
	}

	function updateSort(header) {
		let headerSortOptionsByLabel;
		const hasDropdown = sortOptionsByLabel[header].dropdown;
		const direction = getDirection(header);

		// Set header sort dropdown to default - Commission
		if (hasDropdown) {
			headerSortOptionsByLabel = sortOptionsByLabel[header].dropdown.reduce((obj, item) => {
				const {label, value} = item;
				return {...obj, [label]: {label, value}};
			}, {});

			if (!isEqual(header, currentHeader)) {
				setHeaderSort(headerSortOptionsByLabel.Commission);
			} else {
				const currentSort = cloneDeep(sortBy);
				currentSort.direction = direction;
				setSortBy(currentSort);
			}

			setCurrentHeader(header);
			return;
		}

		const sortOption = sortOptionsByLabel[header];
		sortOption.direction = direction;
		setCurrentHeader(header);
		setSortBy(sortOption);
	}

	function getDirection(header, dropdownDirection=false) {
		let direction;
		// Clicking the same header should sort in the opposite direction
		if (sortBy.label === header && !dropdownDirection) {
			direction = sortBy.direction === 'asc' ? 'desc' : 'asc';
		} else if (dropdownDirection) {
			direction = sortDirection;
		} else {
			direction = sortOptionsByLabel[header].direction;
		}
		return direction;
	}

	function handleClickHeader(event, header, sortable) {
		event.stopPropagation();
		if (sortable) updateSort(header);
	}

	function setHeaderSorting(headerSort) {
		const currentHeaderSort = cloneDeep(headerSort);
		currentHeaderSort.direction = sortDirection;
		setHeaderSort(headerSort);
	}

	const headerRow = !loading ? columnHeaders.map((column, idx) => {
		const headers = column.map((header) => {
			const sortIcon = session.isAdminView && header && sortBy.label === header ? (
				<img className="sort-icon"
					alt="sort icon"
					key={`${header}-sort`}
					src={sortBy.direction === 'asc' ? sortAscIcon : sortDescIcon}
				/>
			) : null;

			const sortDropdown = session.isAdminView && header && sortBy.dropdown
				&& sortBy.label === header ? (
					<div className="header-sort-dropdown"
						key={`${header}-sort-dropdown`}
						onClick={(e) => e.stopPropagation()}>
						<SplitSortDropdown
							sortOptions={sortBy.dropdown}
							sortBy={headerSort}
							setSortBy={(headerSort) => setHeaderSorting(headerSort)}
							parentDirection={sortDirection}
							setParentDirection={setSortDirection}
						/>
					</div>
				) : null;

			const upcomingStrategiesSort = tab === 'upcoming strategies' ?
				!disabledColumns.includes(header) : true;
			const sortable = session.isAdminView
				&& !unsortableColumns.includes(header)
				&& upcomingStrategiesSort
				&& (tab !== 'upcoming strategies' || Object.keys(sortOptionsByLabel).includes(header));

			return (
				<div key={`${header}-dropdown-container`}>
					<div className={getClass('header-label-container', {
						'admin': session.isAdminView,
						'sortable': sortable,
					})}
					key={`${header}-label`}
					onClick={(event) => handleClickHeader(event, header, sortable)}>
						<h2
							key={header}
							className={getClass('header-label', {
								'admin': session.isAdminView,
							})}>
							{header}
						</h2>
						{sortIcon}
						{sortDropdown}
					</div>
				</div>
			);
		});
		return (
			<div key={idx} className={`column ${column.includes('Partner') ? 'merchant-name' : ''}`}>
				{headers}
			</div>
		);
	}) : null;

	const rows = !loading ? dataRows.map((dataRow, idx) => {
		return ['strategies', 'upcoming strategies'].includes(tab) ? (
			<StrategyOverviewRow
				key={idx}
				strategy={dataRow}
				retailer={retailer}
				setModalStrategy={setModalStrategy}
				overview={overview}
			/>
		) : (
			<RetailerOverviewRow
				key={idx}
				retailer={dataRow}
			/>
		);
	}) : null;

	const modal = modalStrategy ? (
		<StrategyInfoModal
			strategy={modalStrategy}
			setModalStrategy={setModalStrategy}
		/>
	) : null;

	const overviewTable = dataRows.length ? (
		<>
			<div className={getClass('row headers', {
				'upcoming-strategies-row': tab === 'upcoming strategies',
				[`${tab}-row`]: tab !== 'upcoming strategies',
				'admin': session.isAdminView,
			})}>
				{headerRow}
			</div>
			{modal}
			{rows}
		</>
	) : (
		<div className="no-results">
			{`No ${tab} at this time.`}
		</div>
	);

	return (
		<div className={`overview-table ${tab} ${overview ? 'all' : 'per'}`}>
			{overviewTable}
		</div>
	);
};

export default OverviewTable;
